import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { CountUp } from '@/components/ui/CountUp';
import { motion } from 'framer-motion';
import { Building2, Award, Globe, Briefcase } from 'lucide-react';

const badges = [
  { icon: <Building2 className="w-5 h-5 text-accent" />, name: 'Building Consulting' },
  { icon: <Award className="w-5 h-5 text-accent" />, name: 'LEED Certified' },
  { icon: <Globe className="w-5 h-5 text-accent" />, name: 'Sustainable Build' },
  { icon: <Briefcase className="w-5 h-5 text-accent" />, name: 'Project Management' },
];

export function StatsSection() {
  const { data: dynStats } = useQuery({
    queryKey: ['portfolioStats'],
    queryFn: async () => {
      const { data } = await api.get('portfolio-stats/');
      return data;
    }
  });

  const getNum = (v?: string) => parseInt(v?.replace(/[^0-9]/g, '') || '0', 10);

  const currentStats = [
    { value: dynStats ? getNum(dynStats.years_experience) : 15, suffix: '+', label: 'Years Experience', desc: 'Decades of trusted expertise' },
    { value: dynStats ? getNum(dynStats.projects_completed) : 500, suffix: '+', label: 'Projects Completed', desc: 'Across all sectors worldwide' },
    { value: dynStats ? getNum(dynStats.client_satisfaction) : 100, suffix: '%', label: 'Client Satisfaction', desc: 'Happy clients, every time' },
  ];

  return (
    <section className="bg-white py-8 md:py-11 relative overflow-hidden">
      {/* Background decorative */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(251,191,36,0.07),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(14,165,233,0.05),transparent_60%)]" />

      <div className="container-custom relative z-10">
        {/* Heading */}
        <div className="text-center mb-4 sm:mb-6 md:mb-8 px-4">
          <motion.div
            className="flex items-center justify-center gap-2 sm:gap-3 mb-2 sm:mb-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="w-6 sm:w-8 h-px bg-amber-400" />
            <span className="text-accent text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] sm:tracking-[0.3em]">By The Numbers</span>
            <div className="w-6 sm:w-8 h-px bg-amber-400" />
          </motion.div>
          <motion.h2
            className="text-2xl sm:text-3xl md:text-5xl font-black text-slate-900 uppercase tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Our Impact <span className="text-accent">in Numbers</span>
          </motion.h2>
          <motion.p
            className="text-slate-500 mt-2 sm:mt-3 text-sm sm:text-base"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Delivering excellence across every project, every time.
          </motion.p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-6 md:mb-7 px-4 sm:px-0">
          {currentStats.map((stat, index) => (
            <motion.div
              key={index}
              className="relative text-center py-4 sm:py-5 md:py-6 px-4 sm:px-6 md:px-8 border border-slate-100 rounded-2xl sm:rounded-3xl shadow-sm bg-white group hover:border-amber-300 hover:shadow-lg transition-all duration-500 overflow-hidden cursor-default"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              {/* Top accent */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 sm:w-16 h-1 bg-linear-to-r from-amber-400 to-orange-400 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              <div className="relative z-10">
                <h3 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter mb-1 sm:mb-2 group-hover:text-accent transition-colors duration-500">
                  <CountUp value={stat.value} duration={2.5} />
                  <span className="text-accent">{stat.suffix}</span>
                </h3>
                <p className="text-[10px] sm:text-xs font-black text-slate-500 uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-1 sm:mb-2">{stat.label}</p>
                <p className="text-[10px] sm:text-xs text-slate-400 font-medium">{stat.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Partner Badges */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 pt-3 sm:pt-4 md:pt-5 border-t border-slate-100 px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="text-[9px] sm:text-[10px] text-slate-400 font-black uppercase tracking-widest mr-1 sm:mr-2">Certified by</span>
          {badges.map((badge, i) => (
            <motion.div
              key={i}
              className="flex items-center gap-1.5 sm:gap-2 bg-slate-50 border border-slate-100 hover:border-amber-300 text-slate-500 hover:text-slate-900 transition-all duration-300 rounded-full px-2.5 sm:px-4 py-1.5 sm:py-2 group cursor-default"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="group-hover:scale-110 transition-transform duration-200">
                {badge.icon}
              </div>
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider">{badge.name}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
